import {
  AppstoreOutlined,
  BankOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownOutlined,
  LogoutOutlined,
  MailOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  TeamOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Alert, Avatar, Button, Empty, Form, Input, Modal, Result, Select, Skeleton, Tag, message } from 'antd';
import BrandLogo from 'assets/img/workplansiq-logo.svg';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant';
import useAuthRepo from 'services/repo/useAuthRepo';
import {
  cancelMockAccessRequest,
  createMockAccessRequest,
  createMockInvitation,
  createMockOrganization,
  getAdminAccessRequests,
  getCurrentUserAccessRequests,
  getSelectedTenant,
  getTenantMemberships,
  reviewMockAccessRequest,
  saveDemoTenantMemberships,
  saveSelectedTenant,
} from 'services/tenant/tenantMembershipService';
import LocalStorageUtils from 'utils/LocalStorage';
import { hasOrganizationPermission, ORGANIZATION_PERMISSIONS } from 'services/tenant/organizationPermissions';

const STATUS_LABELS = {
  active: 'Active',
  pending: 'Pending',
  'temporary-access': 'Temporary Access',
  provisioning: 'Provisioning',
  expired: 'Expired',
  inactive: 'Inactive',
  suspended: 'Suspended',
};

const isSafeAppPath = (value) =>
  value?.startsWith('/') && !value.startsWith('//');

const getInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

const getRemainingDays = (value) => {
  if (!value) return null;
  const end = new Date(value);
  if (Number.isNaN(end.getTime())) return null;
  return Math.max(0, Math.ceil((end.getTime() - Date.now()) / 86400000));
};

const humanizeStatus = (value, fallback = 'Not available') => value
  ? value.toLowerCase().split(/[_-]/).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
  : fallback;

const getActionLabel = (workspace) => {
  if (workspace.status === 'provisioning') return 'View Provisioning';
  if (workspace.status === 'expired') return 'Renew Subscription';
  if (workspace.status === 'suspended') return 'Resolve Billing';
  if (workspace.paymentStatus === 'PENDING_PAYMENT' && workspace.accessStatus !== 'TEMPORARY_ACCESS') return 'Complete Payment';
  return 'Open Organization';
};

const createDemoOrganizations = (currentWorkspace) => {
  const now = Date.now();
  const temporaryAccessEndAt = new Date(now + (9 * 24 * 60 * 60 * 1000)).toISOString();
  const current = {
    ...currentWorkspace,
    paymentStatus: currentWorkspace.paymentStatus || 'PAID',
    accessStatus: currentWorkspace.accessStatus || 'FULL_ACCESS',
    subscriptionStatus: currentWorkspace.subscriptionStatus || 'ACTIVE',
  };

  return [
    current,
    {
      id: 'demo-nusantara-retail',
      name: 'PT Nusantara Retail Indonesia',
      identifier: 'nusantara-retail.worksplaniq.com',
      plan: 'Continuous Workforce Intelligence',
      role: 'Reviewer',
      roleId: '5febd304-7990-4ddd-a9ce-18812aae2a4f',
      status: 'temporary-access',
      subscriptionStatus: 'INVOICE_PENDING',
      paymentStatus: 'PENDING_PAYMENT',
      accessStatus: 'TEMPORARY_ACCESS',
      temporaryAccessEndAt,
      paymentDueAt: temporaryAccessEndAt,
      orgId: 'demo-org-nusantara-retail',
      wcId: 'demo-wc-nusantara-retail',
      source: 'ui-demo',
    },
    {
      id: 'demo-yayasan-cerdas-bangsa',
      name: 'Yayasan Cerdas Bangsa',
      identifier: 'cerdas-bangsa.worksplaniq.com',
      plan: 'Plan not selected',
      role: 'Organization Administrator',
      roleId: '0f418ead-0b68-4af4-b141-0b894503f559',
      status: 'active',
      subscriptionStatus: 'NO_SUBSCRIPTION',
      paymentStatus: 'NOT_STARTED',
      accessStatus: 'EXPLORATION',
      orgId: 'demo-org-cerdas-bangsa',
      wcId: 'demo-wc-cerdas-bangsa',
      source: 'ui-demo',
    },
  ];
};

export default function TenantManagement() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [query, setQuery] = useState('');
  const [plan, setPlan] = useState('all');
  const [view, setView] = useState('grid');
  const [joinOpen, setJoinOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteOrganization, setInviteOrganization] = useState(null);
  const [createdInvitation, setCreatedInvitation] = useState(null);
  const [creatingOrganization, setCreatingOrganization] = useState(false);
  const [joinValue, setJoinValue] = useState('');
  const [submittedAccessRequest, setSubmittedAccessRequest] = useState(null);
  const [accessRequests, setAccessRequests] = useState([]);
  const [adminAccessRequests, setAdminAccessRequests] = useState([]);
  const [createForm] = Form.useForm();
  const [inviteForm] = Form.useForm();
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuthRepo(false);
  const userInfo = LocalStorageUtils.userInfo.get();
  const activeWorkspace = getSelectedTenant();
  const params = new URLSearchParams(location.search);
  const isManagementMode = params.get('mode') === 'manage';
  const showToolbar = tenants.length > 3;

  useEffect(() => {
    if (LocalStorageUtils.checkTokenExpired()) {
      const returnPath = `${location.pathname}${location.search}`;
      navigate(`/?redirectTo=${encodeURIComponent(returnPath)}`, { replace: true });
      return;
    }

    setLoading(true);
    setLoadError(null);

    getTenantMemberships()
      .then((memberships) => {
        const params = new URLSearchParams(location.search);
        const isManagementMode = params.get('mode') === 'manage';
        const continueTo = params.get('continueTo');

        if (!memberships.length) {
          setTenants([]);
          setAccessRequests(getCurrentUserAccessRequests());
          setAdminAccessRequests(getAdminAccessRequests());
          return;
        }
        if (memberships.length === 1 && !isManagementMode) {
          saveSelectedTenant(memberships[0]);
          navigate(
            isSafeAppPath(continueTo) ? continueTo : `${APP_ROUTE_PREFIX}/${RouteConstant.dashboard}`,
            { replace: true },
          );
          return;
        }
        const shouldSeedDemoOrganizations = isManagementMode
          && memberships.length === 1
          && memberships[0]?.source === 'onboarding-mock';
        const visibleMemberships = shouldSeedDemoOrganizations
          ? createDemoOrganizations(memberships[0])
          : memberships;
        if (shouldSeedDemoOrganizations) saveDemoTenantMemberships(visibleMemberships);
        setTenants(visibleMemberships);
        setAccessRequests(getCurrentUserAccessRequests());
        setAdminAccessRequests(getAdminAccessRequests());
      })
      .catch((error) => {
        setTenants([]);
        setLoadError(error?.message || 'We could not load your organization access. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [location.pathname, location.search, navigate, reloadKey]);

  const plans = useMemo(
    () => [...new Set(tenants.map((tenant) => tenant.plan))],
    [tenants]
  );

  const filteredTenants = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return tenants.filter((tenant) => {
      const matchesPlan = plan === 'all' || tenant.plan === plan;
      const matchesQuery = !normalizedQuery ||
        [tenant.name, tenant.identifier, tenant.id]
          .some((value) => String(value || '').toLowerCase().includes(normalizedQuery));

      return matchesPlan && matchesQuery;
    });
  }, [plan, query, tenants]);

  const openTenant = (tenant) => {
    if (tenant.status === 'provisioning') {
      navigate('/onboarding?resume=provisioning');
      return;
    }
    if (tenant.status === 'expired') {
      navigate('/onboarding?mode=renewal');
      return;
    }
    if (tenant.status === 'suspended') {
      window.location.href = 'mailto:support@worksplaniq.com?subject=Resolve%20organization%20billing';
      return;
    }
    if (tenant.paymentStatus === 'PENDING_PAYMENT' && tenant.accessStatus !== 'TEMPORARY_ACCESS') {
      saveSelectedTenant(tenant);
      navigate(`${APP_ROUTE_PREFIX}/subscription?stage=invoice`);
      return;
    }
    if (tenant.status === 'inactive') return;

    saveSelectedTenant(tenant);
    const continueTo = new URLSearchParams(location.search).get('continueTo');
    const destination = isSafeAppPath(continueTo)
      ? continueTo
      : `${APP_ROUTE_PREFIX}/${RouteConstant.dashboard}`;

    navigate(destination, { replace: true });
  };

  const handleLogout = async () => {
    await logout();
    navigate(RouteConstant.login, { replace: true });
  };

  const retryMemberships = () => setReloadKey((value) => value + 1);

  const clearFilters = () => {
    setQuery('');
    setPlan('all');
  };

  const submitAccessRequest = () => {
    try {
      const request = createMockAccessRequest({ organizationReference: joinValue });
      setSubmittedAccessRequest(request);
      setAccessRequests(getCurrentUserAccessRequests());
      message.success('Access request submitted.');
    } catch (error) {
      message.error(error?.message || 'Unable to submit the access request.');
    }
  };

  const closeAccessRequest = () => {
    setJoinValue('');
    setSubmittedAccessRequest(null);
    setJoinOpen(false);
  };

  const cancelAccessRequest = (requestId) => {
    try {
      cancelMockAccessRequest(requestId);
      setAccessRequests(getCurrentUserAccessRequests());
      message.success('Access request cancelled.');
    } catch (error) {
      message.error(error?.message || 'Unable to cancel the request.');
    }
  };

  const reviewAccessRequest = (requestId, decision) => {
    try {
      reviewMockAccessRequest(requestId, decision);
      setAdminAccessRequests(getAdminAccessRequests());
      message.success(`Access request ${decision.toLowerCase()}.`);
    } catch (error) {
      message.error(error?.message || 'Unable to review the request.');
    }
  };

  const closeOrganizationCreation = () => {
    if (creatingOrganization) return;
    setCreateOpen(false);
    createForm.resetFields();
  };

  const startOrganizationCreation = async () => {
    try {
      const values = await createForm.validateFields();
      setCreatingOrganization(true);
      createMockOrganization(values);
      createForm.resetFields();
      setCreateOpen(false);
      navigate(`${APP_ROUTE_PREFIX}/${RouteConstant.workspaceSetup}?step=0`);
    } catch (error) {
      if (!error?.errorFields) {
        message.error('We could not create the organization. Please try again.');
      }
    } finally {
      setCreatingOrganization(false);
    }
  };

  const openMemberInvitation = (organization) => {
    setInviteOrganization(organization);
    setCreatedInvitation(null);
    inviteForm.resetFields();
    setInviteOpen(true);
  };

  const closeMemberInvitation = () => {
    setInviteOpen(false);
    setInviteOrganization(null);
    setCreatedInvitation(null);
    inviteForm.resetFields();
  };

  const submitMemberInvitation = async () => {
    try {
      const values = await inviteForm.validateFields();
      const invitation = createMockInvitation({
        organizationId: inviteOrganization.id,
        inviteeEmail: values.inviteeEmail,
        role: values.role,
      });
      setCreatedInvitation(invitation);
      message.success('Invitation created.');
    } catch (error) {
      if (!error?.errorFields) message.error(error?.message || 'Unable to create invitation.');
    }
  };

  const invitationPath = createdInvitation
    ? `/invitation/${createdInvitation.invitationId}`
    : '';

  return (
    <div className="tenant-selection-page">
      <aside className="tenant-selection-sidebar">
        <img src={BrandLogo} alt="WorkPlansIQ" className="tenant-selection-logo" />
        <div className="tenant-selection-welcome">
          <span>One account</span>
          <strong>Multiple organizations</strong>
          <p>Switch organizations without signing out. Each organization keeps its own plan, billing, and permissions.</p>
        </div>
        <div className="tenant-selection-illustration" aria-hidden="true">
          <BankOutlined />
        </div>
        <div className="tenant-selection-benefits">
          <div><TeamOutlined /><span><strong>Switch anytime</strong>Access all permitted organizations from one account</span></div>
          <div><SafetyCertificateOutlined /><span><strong>Separate billing</strong>Every organization has its own plan and invoice status</span></div>
          <div><CheckCircleOutlined /><span><strong>Role-based access</strong>Your permissions can differ in each organization</span></div>
        </div>
        <p className="tenant-selection-support">Need help? <a href="mailto:support@worksplaniq.com">Contact support</a></p>
      </aside>

      <main className="tenant-selection-main">
        <header className="tenant-selection-topbar">
          <Avatar>{getInitials(userInfo.userFullName || 'User')}</Avatar>
          <span>{userInfo.userFullName || 'WorkPlansIQ User'}</span>
          <Button type="text" icon={<LogoutOutlined />} onClick={handleLogout} aria-label="Logout" />
        </header>

        <section className="tenant-selection-content">
          <div className="tenant-selection-heading">
            <div>
              <span className="tenant-selection-eyebrow">ORGANIZATION MANAGEMENT</span>
              <h1>{isManagementMode ? 'Manage Organizations' : 'Choose an Organization'}</h1>
              <p>{isManagementMode
                ? 'Switch organizations, review your access, or join another organization.'
                : 'Select the organization you want to open.'}</p>
            </div>
            <Button icon={<PlusOutlined />} onClick={() => setJoinOpen(true)}>Join Organization</Button>
          </div>

          <div className="tenant-selection-context-note">
            <SafetyCertificateOutlined />
            <span><strong>Each organization has its own plan and billing.</strong> You can switch organizations without signing in again.</span>
          </div>

          {!loading && !loadError && accessRequests.length ? (
            <section className="tenant-access-requests">
              <div className="tenant-access-requests-heading"><div><span className="tenant-selection-eyebrow">YOUR REQUESTS</span><h2>Organization access requests</h2></div></div>
              <div className="tenant-access-request-list">
                {accessRequests.map((request) => (
                  <article key={request.requestId} className="tenant-access-request-row">
                    <span className="tenant-access-request-icon"><ClockCircleOutlined /></span>
                    <div><strong>{request.organizationName}</strong><small>{request.status === 'PENDING' ? 'Waiting for approval' : humanizeStatus(request.status)}</small></div>
                    <Tag color={request.status === 'APPROVED' ? 'green' : request.status === 'REJECTED' ? 'red' : request.status === 'CANCELLED' ? 'default' : 'gold'}>{request.status}</Tag>
                    {request.status === 'PENDING' ? <Button size="small" onClick={() => cancelAccessRequest(request.requestId)}>Cancel</Button> : null}
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          {!loading && !loadError && adminAccessRequests.some((request) => request.status === 'PENDING') ? (
            <section className="tenant-access-requests tenant-access-requests--admin">
              <div className="tenant-access-requests-heading"><div><span className="tenant-selection-eyebrow">ADMIN REVIEW</span><h2>Pending access requests</h2></div></div>
              <div className="tenant-access-request-list">
                {adminAccessRequests.filter((request) => request.status === 'PENDING').map((request) => (
                  <article key={request.requestId} className="tenant-access-request-row">
                    <span className="tenant-access-request-icon"><TeamOutlined /></span>
                    <div><strong>{request.requesterName || request.requesterEmail}</strong><small>{request.requesterEmail} · {request.organizationName} · Requested role: {request.requestedRole}</small></div>
                    <Tag color="gold">PENDING</Tag>
                    <div className="tenant-access-request-actions"><Button size="small" danger onClick={() => reviewAccessRequest(request.requestId, 'REJECTED')}>Reject</Button><Button size="small" type="primary" onClick={() => reviewAccessRequest(request.requestId, 'APPROVED')}>Approve</Button></div>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          {showToolbar ? <div className="tenant-selection-toolbar">
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search organization name, domain, or code..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              allowClear
            />
            <Select
              value={plan}
              onChange={setPlan}
              suffixIcon={<DownOutlined />}
              options={[
                { value: 'all', label: 'All Plans' },
                ...plans.map((value) => ({ value, label: value })),
              ]}
            />
            <div className="tenant-selection-view-toggle" aria-label="View options">
              <Button type={view === 'grid' ? 'primary' : 'text'} icon={<AppstoreOutlined />} onClick={() => setView('grid')} aria-label="Grid view" />
              <Button type={view === 'list' ? 'primary' : 'text'} icon={<UnorderedListOutlined />} onClick={() => setView('list')} aria-label="List view" />
            </div>
          </div> : null}

          {loading ? (
            <section className="tenant-selection-state tenant-selection-state--loading" aria-busy="true" aria-live="polite">
              <span className="tenant-selection-state-label">Loading organizations...</span>
              <Skeleton active paragraph={{ rows: 8 }} />
            </section>
          ) : loadError ? (
            <section className="tenant-selection-state tenant-selection-state--error" role="alert">
              <Result
                status="error"
                title="Unable to load organizations"
                subTitle={loadError}
                extra={[
                  <Button key="retry" type="primary" icon={<ReloadOutlined />} onClick={retryMemberships}>Try Again</Button>,
                  <Button key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>Logout</Button>,
                ]}
              />
            </section>
          ) : !tenants.length ? (
            <section className="tenant-selection-state tenant-selection-state--empty">
              <span className="tenant-selection-empty-icon" aria-hidden="true"><BankOutlined /></span>
              <h2>You don’t have an organization yet</h2>
              <p>Create a new organization or join an existing organization.</p>
              <div className="tenant-selection-state-actions">
                <Button type="primary" size="large" icon={<BankOutlined />} onClick={() => setCreateOpen(true)}>Create New Organization</Button>
                <Button size="large" icon={<PlusOutlined />} onClick={() => setJoinOpen(true)}>Join an Organization</Button>
                <Button size="large" type="text" icon={<LogoutOutlined />} onClick={handleLogout}>Logout</Button>
              </div>
            </section>
          ) : filteredTenants.length ? (
            <div className={`tenant-selection-list tenant-selection-list--${view}`}>
              {filteredTenants.map((tenant) => {
                const unavailable = tenant.status === 'inactive';
                const canViewBilling = hasOrganizationPermission(tenant, ORGANIZATION_PERMISSIONS.BILLING_VIEW);
                const remainingDays = getRemainingDays(tenant.temporaryAccessEndAt || tenant.temporaryAccessEnd);
                const isCurrent = tenant.id === activeWorkspace?.id;
                return (
                  <article className={`tenant-card${isCurrent ? ' tenant-card--current' : ''}`} key={tenant.id}>
                    <div className="tenant-card-header">
                      <span className="tenant-card-icon"><BankOutlined /></span>
                      <div className="tenant-card-tags">
                        {isCurrent ? <Tag color="blue">CURRENT ORGANIZATION</Tag> : null}
                        <Tag className={`tenant-status tenant-status--${tenant.status}`}>{STATUS_LABELS[tenant.status]}</Tag>
                      </div>
                    </div>
                    <h2>{tenant.name}</h2>
                    <dl>
                      <div><dt>Plan</dt><dd>{tenant.plan}</dd></div>
                      <div><dt>Your Role</dt><dd>{tenant.role}</dd></div>
                      <div><dt>Billing</dt><dd>{canViewBilling ? humanizeStatus(tenant.paymentStatus, 'Not started') : 'Restricted'}</dd></div>
                      <div><dt>Access</dt><dd>{humanizeStatus(tenant.accessStatus, STATUS_LABELS[tenant.status])}</dd></div>
                      {tenant.accessStatus === 'TEMPORARY_ACCESS' && remainingDays !== null
                        ? <div><dt>Access Ends</dt><dd>{remainingDays} days remaining</dd></div>
                        : null}
                    </dl>
                    <div className="tenant-card-actions">
                      <Button block type="primary" disabled={unavailable} onClick={() => openTenant(tenant)}>
                        {unavailable ? 'Unavailable' : isCurrent ? 'Continue to Dashboard' : getActionLabel(tenant).replace('Open', 'Switch to')}
                      </Button>
                      {String(tenant.role || '').trim().toLowerCase() === 'organization administrator' ? (
                        <Button block icon={<MailOutlined />} onClick={() => openMemberInvitation(tenant)}>Invite Member</Button>
                      ) : null}
                    </div>
                  </article>
                );
              })}
              <article className="tenant-card tenant-card--request tenant-card--action">
                <span className="tenant-card-add"><PlusOutlined /></span>
                <h2>Join an Organization</h2>
                <p>Use an invitation code or request access from an organization administrator.</p>
                <Button onClick={() => setJoinOpen(true)}>Request Access</Button>
              </article>
              <article className="tenant-card tenant-card--request tenant-card--action">
                <span className="tenant-card-add"><BankOutlined /></span>
                <h2>Create New Organization</h2>
                <p>Start a new organization with its own plan, billing, members, and data.</p>
                <Button onClick={() => setCreateOpen(true)}>Create Organization</Button>
              </article>
            </div>
          ) : (
            <section className="tenant-selection-state tenant-selection-state--search-empty">
              <Empty description="No organizations match your search" />
              <Button onClick={clearFilters}>Clear Search and Filters</Button>
            </section>
          )}
        </section>
      </main>

      <Modal title="Request Organization Access" open={joinOpen} okText={submittedAccessRequest ? 'Done' : 'Send Request'} okButtonProps={{ disabled: !submittedAccessRequest && !joinValue.trim() }} cancelButtonProps={{ style: submittedAccessRequest ? { display: 'none' } : undefined }} onOk={submittedAccessRequest ? closeAccessRequest : submitAccessRequest} onCancel={closeAccessRequest}>
        {submittedAccessRequest ? (
          <Result status="info" icon={<ClockCircleOutlined />} title="Waiting for approval" subTitle={`Your request to join ${submittedAccessRequest.organizationName} is pending review by an organization administrator.`} />
        ) : <>
          <p>Enter an organization code, domain, or administrator email. This sends a request and does not grant access immediately.</p>
          <Input value={joinValue} onChange={(event) => setJoinValue(event.target.value)} placeholder="Organization code, domain, or admin email" />
        </>}
      </Modal>

      <Modal
        title="Create a New Organization"
        open={createOpen}
        okText="Create and Start Setup"
        confirmLoading={creatingOrganization}
        onOk={startOrganizationCreation}
        onCancel={closeOrganizationCreation}
        maskClosable={!creatingOrganization}
      >
        <p>Enter the initial organization information. You can complete its profile and structure in the next step.</p>
        <Form form={createForm} layout="vertical" requiredMark="optional">
          <Form.Item
            name="name"
            label="Organization Name"
            rules={[{ required: true, whitespace: true, message: 'Enter the organization name.' }]}
          >
            <Input maxLength={120} showCount placeholder="e.g. PT Nusantara Sejahtera" autoFocus />
          </Form.Item>
          <Form.Item name="email" label="Organization Email" rules={[{ type: 'email', message: 'Enter a valid email address.' }]}>
            <Input maxLength={120} placeholder="e.g. admin@company.com" />
          </Form.Item>
        </Form>
        <div className="tenant-create-coming-soon">
          <Tag color="blue">PROGRESSIVE SETUP</Tag>
          <strong>No subscription plan is required now</strong>
          <span>Your organization starts in Exploration mode. You can choose a plan and complete billing later from the Dashboard.</span>
        </div>
      </Modal>

      <Modal
        title={`Invite Member to ${inviteOrganization?.name || 'Organization'}`}
        open={inviteOpen}
        okText={createdInvitation ? 'Done' : 'Create Invitation'}
        cancelButtonProps={{ style: createdInvitation ? { display: 'none' } : undefined }}
        onOk={createdInvitation ? closeMemberInvitation : submitMemberInvitation}
        onCancel={closeMemberInvitation}
      >
        {createdInvitation ? (
          <div className="tenant-invitation-created">
            <Alert type="success" showIcon message="Invitation ready" description={`Send this mock invitation link to ${createdInvitation.inviteeEmail}.`} />
            <label><span>Invitation link</span><Input readOnly value={`${window.location.origin}${invitationPath}`} /></label>
            <Button type="link" onClick={() => navigate(invitationPath)}>Open Invitation Preview</Button>
          </div>
        ) : (
          <Form form={inviteForm} layout="vertical" initialValues={{ role: 'Reviewer' }}>
            <Form.Item name="inviteeEmail" label="Invitee Email" rules={[{ required: true, message: 'Enter the invitee email.' }, { type: 'email', message: 'Enter a valid email address.' }]}>
              <Input prefix={<MailOutlined />} placeholder="member@company.com" />
            </Form.Item>
            <Form.Item name="role" label="Role" rules={[{ required: true }]}>
              <Select options={['Organization Administrator', 'Billing Admin', 'Reviewer', 'Approver', 'Submitter'].map((value) => ({ value, label: value }))} />
            </Form.Item>
            <Alert type="info" showIcon message="Plan and billing remain owned by this organization" description="Accepting this invitation only creates a membership with the selected role." />
          </Form>
        )}
      </Modal>
    </div>
  );
}
